<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useConvexQuery, useConvexMutation } from 'convex-vue';
import { api } from '../../convex/_generated/api';
import type { Id } from '../../convex/_generated/dataModel';
import MessageSquare from './icons/MessageSquare.vue';
import Edit from './icons/Edit.vue';
import Trash2 from './icons/Trash2.vue';
import X from './icons/X.vue';
import ConfirmDialog from './ConfirmDialog.vue';

useI18n();

interface Comment {
  id: string;
  ideaId: string;
  userId: string;
  userName: string;
  content: string;
  createdAt: number;
  updatedAt?: number;
  isEdited?: boolean;
}

interface IdeaWithComments {
  ideaId: Id<'ideas'>;
  ideaTitle: string;
  ideaPerson?: string;
  comments: Comment[];
}

interface Props {
  modelValue: boolean;
  mode: 'idea' | 'workshop';
  ideaId?: Id<'ideas'>;
  ideaTitle?: string;
  workshopId?: Id<'workshops'>;
  currentUserId?: string;
  isOwner?: boolean;
  commentsEnabled?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  isOwner: false,
  commentsEnabled: true,
});

const emit = defineEmits<{
  'update:modelValue': [value: boolean];
}>();

const isOpen = computed({
  get: () => props.modelValue,
  set: value => emit('update:modelValue', value),
});

// Form state
const newCommentContent = ref('');
const editingCommentId = ref<string | null>(null);
const editContent = ref('');
const deleteTargetId = ref<string | null>(null);
const showDeleteConfirm = computed(() => deleteTargetId.value !== null);

const displayedComments = ref<Comment[]>([]);
const displayedIdeasWithComments = ref<IdeaWithComments[]>([]);

// Mutations
const createComment = useConvexMutation(api.comments.create);
const updateComment = useConvexMutation(api.comments.update);
const deleteComment = useConvexMutation(api.comments.remove);

// Fetch comments based on mode
const ideaCommentsQuery = useConvexQuery(api.comments.listByIdea, () =>
  props.mode === 'idea' && props.ideaId ? { ideaId: props.ideaId } : { ideaId: undefined }
);

const workshopCommentsQuery = useConvexQuery(api.comments.listByWorkshop, () =>
  props.mode === 'workshop' && props.workshopId
    ? { workshopId: props.workshopId }
    : { workshopId: undefined }
);

const isLoading = computed(() =>
  props.mode === 'idea' ? ideaCommentsQuery.isPending.value : workshopCommentsQuery.isPending.value
);

watch(
  () => [
    isOpen.value,
    props.mode,
    props.ideaId,
    ideaCommentsQuery.data.value,
    ideaCommentsQuery.isPending.value,
  ],
  ([open, mode, ideaId, data, isPending]) => {
    if (!open || mode !== 'idea' || !ideaId || isPending || data === undefined) {
      displayedComments.value = [];
      return;
    }
    const ideaData = data as Comment[];
    const matches = ideaData.every(comment => comment.ideaId === ideaId);
    displayedComments.value = matches ? ideaData : [];
  },
  { immediate: true }
);

watch(
  () => [
    isOpen.value,
    props.mode,
    props.workshopId,
    workshopCommentsQuery.data.value,
    workshopCommentsQuery.isPending.value,
  ],
  ([open, mode, workshopId, data, isPending]) => {
    if (!open || mode !== 'workshop' || !workshopId || isPending || data === undefined) {
      displayedIdeasWithComments.value = [];
      return;
    }
    displayedIdeasWithComments.value = data as IdeaWithComments[];
  },
  { immediate: true }
);

const comments = computed(() => displayedComments.value);
const ideasWithComments = computed(() => displayedIdeasWithComments.value);

// Format timestamp
function formatTime(timestamp: number): string {
  const date = new Date(timestamp);
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// Check if user can edit/delete a comment
function canEditComment(comment: Comment): boolean {
  return comment.userId === props.currentUserId;
}

function canDeleteComment(comment: Comment): boolean {
  return comment.userId === props.currentUserId || props.isOwner;
}

// Create new comment
async function handleCreateComment() {
  if (!newCommentContent.value.trim() || !props.ideaId) return;

  try {
    await createComment.mutate({
      ideaId: props.ideaId,
      content: newCommentContent.value.trim(),
    });
    newCommentContent.value = '';
    // Convex queries auto-update, no need to refetch
  } catch (e) {
    console.error('Failed to create comment:', e);
  }
}

// Start editing
function startEdit(comment: Comment) {
  editingCommentId.value = comment.id;
  editContent.value = comment.content;
}

// Cancel editing
function cancelEdit() {
  editingCommentId.value = null;
  editContent.value = '';
}

// Save edit
async function handleUpdateComment() {
  if (!editingCommentId.value || !editContent.value.trim()) return;

  try {
    await updateComment.mutate({
      commentId: editingCommentId.value as Id<'comments'>,
      content: editContent.value.trim(),
    });
    editingCommentId.value = null;
    editContent.value = '';
    // Convex queries auto-update, no need to refetch
  } catch (e) {
    console.error('Failed to update comment:', e);
  }
}

// Delete comment
function requestDelete(commentId: string) {
  deleteTargetId.value = commentId;
}

async function confirmDelete() {
  if (!deleteTargetId.value) return;

  try {
    await deleteComment.mutate({
      commentId: deleteTargetId.value as Id<'comments'>,
    });
    deleteTargetId.value = null;
    // Convex queries auto-update, no need to refetch
  } catch (e) {
    console.error('Failed to delete comment:', e);
  }
}

function cancelDelete() {
  deleteTargetId.value = null;
}

function close() {
  isOpen.value = false;
}

function handleKeydown(event: KeyboardEvent) {
  if (event.key === 'Escape' && isOpen.value) {
    close();
  }
}

onMounted(() => {
  document.addEventListener('keydown', handleKeydown);
});

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeydown);
});

// Reset form state when modal opens
watch(isOpen, open => {
  if (open) {
    newCommentContent.value = '';
    editingCommentId.value = null;
    editContent.value = '';
    return;
  }
  displayedComments.value = [];
  displayedIdeasWithComments.value = [];
});
</script>

<template>
  <Teleport to="body">
    <div
      v-if="isOpen"
      class="fixed inset-0 z-50 flex items-center justify-center p-4"
      @click.self="close"
    >
      <div class="absolute inset-0 bg-black/50" @click="close"></div>

      <div
        class="relative bg-[#d4c4a8] rounded-lg w-full max-w-2xl max-h-[85vh] shadow-2xl flex flex-col overflow-hidden"
        style="
          background-image: url(&quot;data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='corkNoise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.6' numOctaves='5' stitchTiles='stitch'/%3E%3CfeColorMatrix type='saturate' values='0'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23corkNoise)'/%3E%3C/svg%3E&quot;);
        "
      >
        <div class="absolute inset-0 bg-black/5 pointer-events-none"></div>

        <!-- Header -->
        <div class="p-6 border-b border-black/10 relative z-10 bg-[#c9b896]/80">
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-3">
              <MessageSquare class="w-6 h-6 text-gray-700" />
              <h2 class="text-xl font-bold text-gray-800">
                <template v-if="mode === 'idea'">
                  {{ $t('comments.title') }}
                  <span v-if="ideaTitle" class="text-base font-normal text-gray-600">
                    - {{ ideaTitle }}
                  </span>
                </template>
                <template v-else>
                  {{ $t('comments.allComments') }}
                </template>
              </h2>
            </div>
            <button
              @click="close"
              class="cursor-pointer text-gray-700 hover:text-gray-900 transition-colors"
            >
              <X class="w-6 h-6" />
            </button>
          </div>
        </div>

        <!-- Content -->
        <div class="flex-1 overflow-y-auto p-6 space-y-4 relative z-10">
          <div v-if="isLoading" class="text-center py-8">
            <p class="text-gray-600">{{ $t('common.loading') }}</p>
          </div>

          <!-- Single Idea Mode -->
          <template v-if="mode === 'idea'">
            <!-- New Comment Form -->
            <div v-if="commentsEnabled" class="mb-6">
              <div class="rounded-lg border border-black/10 bg-white/90 p-4">
                <textarea
                  v-model="newCommentContent"
                  :placeholder="$t('comments.addPlaceholder')"
                  class="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 resize-none"
                  rows="3"
                  @keyup.ctrl.enter="handleCreateComment"
                />
                <div class="mt-3 flex justify-end">
                  <button
                    @click="handleCreateComment"
                    :disabled="!newCommentContent.trim()"
                    class="cursor-pointer px-4 py-2 bg-(--color-primary) text-white rounded-lg hover:bg-opacity-90 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {{ $t('comments.add') }}
                  </button>
                </div>
              </div>
            </div>

            <!-- Comments Disabled Message -->
            <div v-else class="text-center py-4 text-gray-600 bg-white/50 rounded-lg">
              {{ $t('comments.disabled') }}
            </div>

            <!-- Comments List -->
            <div v-if="comments.length === 0 && !isLoading" class="text-center py-8 text-gray-600">
              {{ $t('comments.noComments') }}
            </div>

            <div class="space-y-3">
              <div
                v-for="comment in comments"
                :key="comment.id"
                class="rounded-lg border border-black/10 bg-white/90 p-4"
              >
                <!-- Edit Mode -->
                <div v-if="editingCommentId === comment.id">
                  <textarea
                    v-model="editContent"
                    class="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 resize-none"
                    rows="3"
                  />
                  <div class="mt-3 flex justify-end gap-2">
                    <button
                      @click="cancelEdit"
                      class="cursor-pointer px-3 py-1.5 text-gray-600 hover:text-gray-800 transition-colors text-sm"
                    >
                      {{ $t('common.cancel') }}
                    </button>
                    <button
                      @click="handleUpdateComment"
                      :disabled="!editContent.trim()"
                      class="cursor-pointer px-4 py-1.5 bg-(--color-primary) text-white rounded-lg hover:bg-opacity-90 transition-colors text-sm font-medium disabled:opacity-50"
                    >
                      {{ $t('comments.save') }}
                    </button>
                  </div>
                </div>

                <!-- View Mode -->
                <div v-else>
                  <div class="flex items-start justify-between gap-3">
                    <div class="flex-1 min-w-0">
                      <p class="text-gray-800 whitespace-pre-wrap">{{ comment.content }}</p>
                      <div class="mt-2 flex items-center gap-2 text-xs text-gray-500">
                        <span class="font-medium">{{ comment.userName }}</span>
                        <span>•</span>
                        <span>{{ formatTime(comment.createdAt) }}</span>
                        <span v-if="comment.isEdited" class="italic">
                          ({{ $t('comments.edited') }})
                        </span>
                      </div>
                    </div>

                    <!-- Actions -->
                    <div class="flex items-center gap-1 shrink-0">
                      <button
                        v-if="canEditComment(comment)"
                        @click="startEdit(comment)"
                        class="cursor-pointer p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                        :title="$t('comments.edit')"
                      >
                        <Edit class="w-4 h-4" />
                      </button>
                      <button
                        v-if="canDeleteComment(comment)"
                        @click="requestDelete(comment.id)"
                        class="cursor-pointer p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                        :title="$t('comments.delete')"
                      >
                        <Trash2 class="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </template>

          <!-- Workshop Mode (Organizer View) -->
          <template v-else>
            <div
              v-if="ideasWithComments.length === 0 && !isLoading"
              class="text-center py-8 text-gray-600"
            >
              {{ $t('comments.noCommentsWorkshop') }}
            </div>

            <div class="space-y-4">
              <div
                v-for="idea in ideasWithComments"
                :key="idea.ideaId"
                class="rounded-lg border border-black/10 bg-white/90 overflow-hidden"
              >
                <!-- Idea Header -->
                <div class="bg-amber-50/50 p-3 border-b border-black/5">
                  <div class="font-semibold text-gray-800">{{ idea.ideaTitle }}</div>
                  <div v-if="idea.ideaPerson" class="text-gray-500 text-sm">
                    {{ idea.ideaPerson }}
                  </div>
                </div>

                <!-- Comments for this idea -->
                <div class="p-3 space-y-3">
                  <div
                    v-for="comment in idea.comments"
                    :key="comment.id"
                    class="border-l-2 border-gray-200 pl-3 py-1"
                  >
                    <p class="text-sm text-gray-800">{{ comment.content }}</p>
                    <div class="mt-1 flex items-center gap-2 text-xs text-gray-500">
                      <span class="font-medium">{{ comment.userName }}</span>
                      <span>•</span>
                      <span>{{ formatTime(comment.createdAt) }}</span>
                      <span v-if="comment.isEdited" class="italic">
                        ({{ $t('comments.edited') }})
                      </span>
                      <button
                        v-if="canDeleteComment(comment)"
                        @click="requestDelete(comment.id)"
                        class="cursor-pointer ml-auto text-gray-400 hover:text-red-600 transition-colors"
                        :title="$t('comments.delete')"
                      >
                        <Trash2 class="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </template>
        </div>
      </div>

      <!-- Delete Confirmation Dialog -->
      <ConfirmDialog
        v-model="showDeleteConfirm"
        :title="$t('comments.deleteConfirmTitle')"
        :message="$t('comments.deleteConfirmMessage')"
        :confirm-text="$t('common.delete')"
        :cancel-text="$t('common.cancel')"
        confirm-color="destructive"
        @confirm="confirmDelete"
        @cancel="cancelDelete"
      />
    </div>
  </Teleport>
</template>
